import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Trans } from '@/UI/Trans/Trans';
import { styled } from '@/UI/theme.exports';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import './ErrorPage.css';
import { ErrorSidebar } from '@/components/Common/Error/ErrorSidebar';
import { I18N_KEYS } from '@/language/keys';
import { ChatCircleIcon } from '@/UI/Icons/ChatCircleIcon';
import { MonitorIcon } from '@/UI/Icons/MonitorIcon';
import { RefreshIcon } from '@/UI/Icons/RefreshIcon';
import { RestartIcon } from '@/UI/Icons/RestartIcon';
import { WarningCircleIcon } from '@/UI/Icons/WarningCircleIcon';
import { openIntercom } from '@/providers/intercom';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { color } from '@/colors';
import { FlexColCenHor } from '@/UI/styles';
import { Box } from '@/UI/Box/Box';
import { Link } from '@/UI/Link/Link';
import { ButtonTextPlain } from '@/UI/ButtonTextPlain/ButtonTextPlain';
import { TextSmMd, TextSmRg } from '@/UI/AppTypography/AppTypography';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';

const K = I18N_KEYS.GPU_DRIVER_ERROR_PAGE;

export const GPUDriverErrorPage = () => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  useEffect(() => {
    track('gpu_driver_error_page_viewed', {}, TrackTypeEnum.BI);
  }, [track]);

  return (
    <div className="error-page">
      <ErrorSidebar />
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: 1, height: 1 }}>
        <FlexColCenHor
          sx={{
            pt: '36px',
            pb: 3,
            px: 3,
            background: `linear-gradient(${color.Weavy_Error_08_T}, ${color.Weavy_Error_08_T}), ${color.Black100}`,
            border: `1px solid ${color.Weavy_Error_64_T}`,
            borderRadius: '8px',
            width: 1,
            maxWidth: 440,
            gap: 3,
          }}
        >
          <FlexColCenHor sx={{ gap: 1 }}>
            <WarningCircleIcon style={{ width: 24, height: 24, color: token.textDanger }} />
            <TextSmMd color={token.textDanger}>{t(K.TITLE)}</TextSmMd>
          </FlexColCenHor>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', width: 1 }}>
            <TextSmRg color={token.text}>{t(K.DESCRIPTION)}</TextSmRg>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', width: 1 }}>
              <StepCard icon={<RefreshIcon width={16} height={16} />} title={t(K.STEP_REFRESH_TITLE)}>
                {t(K.STEP_REFRESH_DESCRIPTION)}
              </StepCard>
              <StepCard icon={<MonitorIcon width={16} height={16} />} title={t(K.STEP_HARDWARE_ACCELERATION_TITLE)}>
                <Trans
                  i18nKey={K.STEP_HARDWARE_ACCELERATION_DESCRIPTION}
                  components={{
                    learnHow: (
                      <Link
                        href={EXTERNAL_LINKS.browserConfiguration}
                        target="_blank"
                        rel="noreferrer"
                        underline="always"
                        sx={{ color: token.textSecondary, textDecorationColor: 'inherit' }}
                        onClick={() => track('gpu_driver_error_learn_how_clicked', {}, TrackTypeEnum.BI)}
                      />
                    ),
                  }}
                />
              </StepCard>
              <StepCard icon={<RestartIcon />} title={t(K.STEP_RESTART_TITLE)}>
                {t(K.STEP_RESTART_DESCRIPTION)}
              </StepCard>
              <StepCard icon={<ChatCircleIcon width={16} height={16} />} title={t(K.STEP_CONTACT_SUPPORT_TITLE)}>
                <Trans
                  i18nKey={K.STEP_CONTACT_SUPPORT_DESCRIPTION}
                  components={{
                    supportEmail: (
                      <ButtonTextPlain onClick={openIntercom} variant="body-sm-rg" color={token.textSecondary} />
                    ),
                  }}
                />
              </StepCard>
            </Box>
          </Box>
        </FlexColCenHor>
      </Box>
    </div>
  );
};

const StepCardRoot = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  padding: 16,
  backgroundColor: token.bgTransparent,
  borderRadius: 8,
  textAlign: 'left',
});

function StepCard({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  const isFpl = useFplMigrationFlag();

  return (
    <StepCardRoot sx={{ gap: isFpl ? '4px' : '8px' }}>
      <Box
        sx={{
          alignItems: 'flex-start',
          color: isFpl ? undefined : color.White64_T,
          display: 'flex',
          flexShrink: 0,
          height: isFpl ? 24 : 16,
          justifyContent: 'center',
          mt: isFpl ? '-4px' : '1px',
          width: isFpl ? 24 : 16,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <TextSmMd color={token.text}>{title}</TextSmMd>
        <TextSmRg color={token.textSecondary}>{children}</TextSmRg>
      </Box>
    </StepCardRoot>
  );
}
